import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Briefcase, Sparkles, Inbox, CheckCircle2, AlertTriangle } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import StatTile from "../../components/StatTile";
import CountdownBadge from "../../components/CountdownBadge";
import { listMyOffers, acceptOffer, rejectOffer, getMySkills } from "../../services/archMarketplace";

const humanize = (s) => (s || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const fmtINR = (n) => (n == null ? null : `₹${Number(n).toLocaleString("en-IN")}`);

// Colors for the PROJECT lifecycle status shown on assigned cards.
const PROJECT_PILL = {
  ASSIGNED: "bg-blue-100 text-blue-700",
  IN_PROGRESS: "bg-blue-100 text-blue-700",
  IN_REVIEW: "bg-purple-100 text-purple-700",
  CHANGES_REQUESTED: "bg-orange-100 text-orange-700",
  COMPLETED: "bg-emerald-100 text-emerald-700",
};

function FreelancerDashboard() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [skillCount, setSkillCount] = useState(null); // null = unknown/loading
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    const data = await listMyOffers(false);
    setOffers(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    let mounted = true;
    Promise.all([load(), getMySkills().catch(() => null)])
      .then(([, mine]) => {
        if (mounted && mine) setSkillCount((mine.skills || []).length);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const actionable = offers.filter((o) => o.status === "PENDING" && o.offeredAt);
  const assigned = offers.filter((o) => o.status === "ACCEPTED");

  const respond = async (offerId, accept) => {
    setBusyId(offerId);
    try {
      if (accept) await acceptOffer(offerId);
      else await rejectOffer(offerId);
      toast.success(accept ? "Offer accepted! The project is now yours." : "Offer declined.");
      await load();
    } catch (e) {
      toast.error(e?.detail || e?.response?.data?.detail || "Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="My Work"
        subtitle="Respond to offers and deliver on assigned projects"
        icon={Briefcase}
        actions={
          <button
            onClick={() => navigate("/freelancer/skills")}
            className="inline-flex items-center gap-2 rounded-md bg-white/95 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-white"
          >
            <Sparkles size={16} /> My Skills
          </button>
        }
      />

      {/* Skills reminder — strong warning when no skills are set (they can't be matched at all). */}
      {!loading && skillCount === 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4">
          <AlertTriangle size={20} className="mt-0.5 shrink-0 text-rose-500" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-rose-800">You won't receive any offers yet</p>
            <p className="text-sm text-rose-700">
              Add and rate your skills so we can match you to projects. Without skills, you can't be matched.
            </p>
          </div>
          <button
            onClick={() => navigate("/freelancer/skills")}
            className="shrink-0 rounded-md bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-rose-700"
          >
            Add Skills
          </button>
        </div>
      )}
      {!loading && skillCount > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <Sparkles size={18} className="mt-0.5 shrink-0 text-amber-500" />
          <p className="flex-1 text-sm text-amber-800">
            Keep your skills up to date so you get matched to the right projects.{" "}
            <button
              onClick={() => navigate("/freelancer/skills")}
              className="font-semibold text-amber-900 underline hover:text-amber-950"
            >
              Update skills
            </button>
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatTile icon={Inbox} label="Open Offers" value={actionable.length} accent="amber" loading={loading} />
        <StatTile icon={CheckCircle2} label="Assigned Projects" value={assigned.length} accent="emerald" loading={loading} />
      </div>

      {/* Incoming offers */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Incoming Offers</h3>
        {loading ? (
          <p className="text-gray-400">Loading…</p>
        ) : actionable.length === 0 ? (
          <p className="text-sm text-gray-400">No open offers right now. Keep your skills up to date to get matched.</p>
        ) : (
          <div className="space-y-3">
            {actionable.map((o) => (
              <div key={o.offerId} className="rounded-lg border border-amber-200 bg-amber-50/40 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="font-semibold text-gray-800">{o.projectName}</h4>
                    <p className="mt-1 text-sm text-gray-600 line-clamp-2">{o.description}</p>
                    {fmtINR(o.freelancerAmount) && (
                      <p className="mt-2 text-sm font-semibold text-emerald-700">
                        You earn: {fmtINR(o.freelancerAmount)}
                      </p>
                    )}
                  </div>
                  {o.offerExpiresAt && <CountdownBadge deadline={o.offerExpiresAt} label="Respond in" size="sm" />}
                </div>
                <div className="mt-3 flex gap-3">
                  <button
                    onClick={() => respond(o.offerId, true)}
                    disabled={busyId === o.offerId}
                    className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => respond(o.offerId, false)}
                    disabled={busyId === o.offerId}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assigned projects */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Assigned Projects</h3>
        {assigned.length === 0 ? (
          <p className="text-sm text-gray-400">No assigned projects yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assigned.map((o) => (
              <button
                key={o.offerId}
                onClick={() => navigate(`/freelancer/projects/${o.archProjectId}`)}
                className="text-left rounded-lg border border-gray-200 bg-white p-4 hover:shadow-md transition-shadow"
              >
                <h4 className="font-semibold text-gray-800 line-clamp-1">{o.projectName}</h4>
                <p className="mt-1 text-sm text-gray-500 line-clamp-2">{o.description}</p>
                <span className={`mt-3 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${PROJECT_PILL[o.projectStatus] || "bg-gray-100 text-gray-600"}`}>
                  {humanize(o.projectStatus || o.status)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default FreelancerDashboard;
